import * as React from "react";
import AddShopDialog from "../components/AddShopDialog";
import ShopInfo from "../components/ShopInfo";
import { useSWRConfig } from "swr";
import { useCurrentUser } from "../globalState";
import { useActiveInfluencer } from "../hooks";
import { useNotification } from "../services/Notifications";
import { Avatar, Box, Popover, Typography, UnfoldMoreIcon } from "../themeV2";
import { getInitials } from "../util/text";
import ShopSwitcherPopover from "./ShopSwitcherPopover";
import styles from "./NavigationShopHeader.module.css";

const isShopsKey = (key) =>
  typeof key === "string" && key.startsWith("/api/dashboard/shops");

export default function NavigationShopHeader() {
  const activeInfluencer = useActiveInfluencer();
  const [open, setOpen] = React.useState(false);
  const [addingShop, setAddingShop] = React.useState(false);
  const notify = useNotification();

  const currentUser = useCurrentUser();
  const { mutate } = useSWRConfig();

  const handleAddShop = React.useCallback(() => {
    setAddingShop(true);
  }, []);

  const handleAddShopClose = React.useCallback(
    (done, opts) => {
      if (opts?.shouldRefresh) {
        notify({ message: "New shop created!" });
        mutate(isShopsKey);
      }
      done();
      setAddingShop(false);
    },
    [mutate, notify],
  );
  const hasMultipleShops =
    currentUser.isAdmin ||
    currentUser.isSuperadmin ||
    (currentUser.shops?.length ?? 0) > 1;

  const avatarContent = (
    <Avatar src={activeInfluencer?.avatarUrl} className={styles.avatar}>
      {getInitials(activeInfluencer?.name)}
    </Avatar>
  );

  const metaContent = (
    <Box direction="column" style={{ flex: 1, minWidth: 0 }}>
      <Typography variant="body-lg" truncate className={styles.name}>
        {activeInfluencer?.name}
      </Typography>
      <Typography variant="label-lg" color="variant" truncate>
        {activeInfluencer?.ownerEmail ?? "No owner"}
      </Typography>
    </Box>
  );

  if (!hasMultipleShops) {
    return (
      <div className={styles.shopSwitch}>
        <div className={styles.singleShop}>
          {avatarContent}
          {metaContent}
        </div>
        <ShopInfo activeInfluencer={activeInfluencer} />
      </div>
    );
  }

  return (
    <div className={styles.shopSwitch}>
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger className={styles.shopCard} aria-label="Switch shop">
          {avatarContent}
          {metaContent}
          <UnfoldMoreIcon className={styles.unfoldIcon} />
        </Popover.Trigger>
        <Popover.Popup
          side="bottom"
          align="start"
          sideOffset={4}
          className={styles.switcherPopup}
        >
          <ShopSwitcherPopover
            onClose={() => setOpen(false)}
            onAddShop={handleAddShop}
          />
        </Popover.Popup>
      </Popover.Root>

      <ShopInfo activeInfluencer={activeInfluencer} />

      {addingShop && <AddShopDialog onClose={handleAddShopClose} />}
    </div>
  );
}
